import { Component, Element, h, Host, Prop, State, Watch } from "@stencil/core";
import { AutosuggestItem } from "./autosuggest-response";
import { getSuggestions } from "./get-suggestions";
import { getUniqueId } from "../../utils/unique-id";
import { escapeRegexCharacters } from "../../utils/escapeRegexCharacters";
import { logger } from "../../utils/console";

@Component({
    tag: "ux-autosuggest-input",
    styleUrl: "autosuggest-input.scss",
    shadow: false
})
export class AutosuggestInput {
    private inputElement: HTMLInputElement;
    private effectiveInputElement: HTMLInputElement;

    /**
     * Placeholder text for input
     */
    @Prop() readonly placeholder?: string = "";
    /**
     * Name attribute for input
     */
    @Prop() readonly name?: string = "";
    /**
     * Id attribute for input. A unique id will be generated if this is empty
     */
    @Prop() readonly inputId?: string = "";
    /**
     * Label for input
     */
    @Prop() readonly label?: string = "";
    /**
     * Additional info label for the input field
     */
    @Prop() readonly labelInfo: string = "";
    /**
     * URL for autosuggest-API
     */
    @Prop() readonly url?: string = "";
    /**
     * Indicates if the autosuggest input is disabled
     */
    @Prop() readonly disabled: boolean = false;
    /**
     * Indicates if the autosuggest input is required
     */
    @Prop() readonly required: boolean = false;
    /**
     * Indicates if the autosuggest input has an error
     */
    @Prop() readonly error: boolean = false;
    /**
     * Error message that is rendered if component has an error
     * @deprecated type will be changed to string in the next major version
     */
    @Prop() readonly errorMsg: string | null = null;
    /**
     * Suggestions set statically, without an API request
     */
    @Prop() readonly staticSuggestions: AutosuggestItem[] = [];

    @State() suggestions: AutosuggestItem[] = [];
    @State() input = "";
    @State() isHidden = true;
    @State() id = "";
    @State() hasStaticSuggestions = false;

    @Element() domNode!: HTMLUxAutosuggestInputElement;

    @Watch("inputId")
    componentWillLoad(): void {
        const { inputId } = this;
        this.id = inputId || getUniqueId();
    }

    componentDidLoad(): void {
        this.setHasStaticSuggestionsState();
    }

    @Watch("staticSuggestions")
    setHasStaticSuggestionsState(): void {
        if (this.staticSuggestions.length === 0) {
            return;
        }
        const checkSuggestions = [...this.staticSuggestions];
        if (
            checkSuggestions.filter((staticSuggestion: AutosuggestItem) => {
                return staticSuggestion.description !== undefined;
            }).length === 0
        ) {
            this.hasStaticSuggestions = true;
        }
    }

    #onInput(event: Event): void {
        const { value } = event.target as HTMLInputElement;
        this.input = value;
        if (this.staticSuggestions.length) {
            const suggestionValue = this.#getValueFromSuggestion(value);
            this.effectiveInputElement.value = suggestionValue || value;
            if (value === "") {
                this.suggestions = this.staticSuggestions;
            }
            this.suggestions = this.#filterStaticSuggestions(value);
            return;
        }
        if (value === "" || this.url === "") {
            this.suggestions = [];
            return;
        }
        getSuggestions(`${this.url}?q=${value}`)
            .then((response) => (this.suggestions = response || []))
            .catch((error) => logger.error(error));
    }

    #filterStaticSuggestions = (value: string): AutosuggestItem[] => {
        return this.staticSuggestions.filter((staticSuggestion: AutosuggestItem) =>
            (staticSuggestion.label || staticSuggestion.value).toLowerCase().includes(value.toLowerCase())
        );
    };

    #getValueFromSuggestion = (value: string): string | null => {
        const suggestion = this.staticSuggestions.find(
            (suggestion) => (suggestion.label?.toLowerCase() || suggestion.value.toLowerCase()) === value.toLowerCase().trim()
        );
        if (suggestion !== undefined) {
            return suggestion.value;
        } else {
            return null;
        }
    };

    #onBlur(): void {
        this.isHidden = true;
    }

    #onFocus(): void {
        this.isHidden = false;
        this.input = this.inputElement.value;
        if (this.staticSuggestions.length) {
            if (this.inputElement.value === "") {
                this.suggestions = this.staticSuggestions;
            }
            this.suggestions = this.#filterStaticSuggestions(this.inputElement.value);
            return;
        }
    }

    #onSuggestionItemClick(event: Event, value: string): void {
        event.preventDefault();
        this.inputElement.value = value;
        if (this.hasStaticSuggestions) {
            const suggestionItem = this.staticSuggestions.find((suggestion) => {
                return suggestion.value === value;
            });
            if (!suggestionItem) {
                return;
            }
            this.inputElement.value = suggestionItem.label || "";
            this.effectiveInputElement.value = suggestionItem.value;
        }
        this.suggestions = [];
    }

    #getPatternAttribute = (): string | null => {
        if (!this.staticSuggestions.length) {
            return null;
        }
        return `^(${this.staticSuggestions.map((suggestion) => escapeRegexCharacters(suggestion.value)).join("|")})$`;
    };

    render() {
        const baseClass = "ux-autosuggest-input";
        const {
            placeholder,
            name,
            suggestions,
            id,
            input,
            label,
            labelInfo,
            isHidden,
            disabled,
            required,
            error,
            errorMsg,
            hasStaticSuggestions
        } = this;

        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--disabled`]: disabled,
                    [`${baseClass}--error`]: error
                }}
                onFocusout={() => this.#onBlur()}
            >
                <ux-input-label
                    text={label}
                    inputId={id}
                    error={error}
                    errorMsg={errorMsg as string}
                    required={required}
                    labelInfo={labelInfo}
                />
                <div class={`${baseClass}__input-container`}>
                    {hasStaticSuggestions && (
                        <input
                            name={name}
                            type="text"
                            style={{ display: "none" }}
                            required={required}
                            pattern={this.#getPatternAttribute() || undefined}
                            ref={(el) => (this.effectiveInputElement = el as HTMLInputElement)}
                            aria-hidden="true"
                        />
                    )}
                    <input
                        class="ux-input"
                        name={hasStaticSuggestions ? undefined : name}
                        type="text"
                        placeholder={placeholder}
                        autocomplete="off"
                        autocapitalize="off"
                        id={id}
                        disabled={disabled}
                        required={hasStaticSuggestions ? undefined : required}
                        onInput={(event) => this.#onInput(event)}
                        onFocus={() => this.#onFocus()}
                        ref={(el) => (this.inputElement = el as HTMLInputElement)}
                    />
                    <div
                        class={{
                            [`${baseClass}__suggestions`]: true,
                            [`${baseClass}__suggestions--hidden`]: isHidden || this.suggestions.length === 0,
                            [`${baseClass}__suggestions--slim`]: this.hasStaticSuggestions
                        }}
                    >
                        {suggestions?.map(({ value, description, image, label: suggestionLabel }) => (
                            <a
                                class={`${baseClass}__suggestion-link`}
                                href="#"
                                onFocus={() => this.#onFocus()}
                                onClick={(event) => this.#onSuggestionItemClick(event, value)}
                            >
                                <ux-autosuggest-item
                                    text={!hasStaticSuggestions ? value : suggestionLabel}
                                    input={input}
                                    subline={description}
                                    image={image}
                                    slim={hasStaticSuggestions}
                                />
                            </a>
                        ))}
                    </div>
                </div>
            </Host>
        );
    }
}
